import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  TrendConfigs,
  TrendsOverTimeChart,
} from "../../../components/TrendOverTimeChart/TrendsOverTimeChart.component";
import {
  AggregationFn,
  BrushRange,
  MetricConfig,
} from "../../../components/TrendOverTimeChart/TrendsOverTimeChart.types";
import { useAllMatches, useCityEscapesDatasource } from "../../../hooks";
import { useProductsDatasource } from "../../../hooks/products.hooks";
import { MatchFilter } from "../../../libs/statsFilters";
import { WorkerQueryTransferModes } from "../../../libs/workers/consts";
import { GlobalStatsSelection } from "../../../types/stats";
import { AGGREGATION_KEYS } from "./CityEscapeTrendsOverTimeChart.consts";
import { useCityEscapeTrendsOverTimeChartEntries } from "./CityEscapeTrendsOverTimeChart.hooks";
import { getMetrics } from "./CityEscapeTrendsOverTimeChart.service";
import {
  AggregationKeyInput,
  CityEscapeOverTimeChartAggregationKey,
} from "./CityEscapeTrendsOverTimeChart.types";

const AGGREGATION_FN_LABELS: Record<AggregationFn, string> = {
  sum: "Somma",
  count: "Totale",
  average: "Media",
};

const buildYAxisLabel = (keyLabel: string, fn: AggregationFn): string =>
  `${keyLabel} (${AGGREGATION_FN_LABELS[fn]})`;

export interface CityEscapeMetricsChartProps {
  dataType?: "cityEscape" | "product";
  selection?: GlobalStatsSelection;
  filters?: MatchFilter;
  loading?: boolean;
  pending?: boolean;
  opts?: {
    grid?: any;
    defaultAggregationPeriod?: "daily" | "weekly" | "monthly";
    defaultAggregationKey?: CityEscapeOverTimeChartAggregationKey;
    defaultAggregationMode?: "stack" | "compare";
  };
}

export function CityEscapesTrendsOverTimeChart({
  dataType = "cityEscape",
  selection,
  filters,
  pending,
  opts: {
    grid = { vertical: true, horizontal: true },
    defaultAggregationKey = "start",
    defaultAggregationPeriod = "monthly",
    defaultAggregationMode = "compare",
  } = {},
}: CityEscapeMetricsChartProps) {
  const [comparandAggregationKey, setComparandAggregationKey] =
    useState<CityEscapeOverTimeChartAggregationKey>("start");
  
  // Track enabled state of metrics to preserve selection across aggregation changes
  const [enabledMetrics, setEnabledMetrics] = useState<Set<string>>(new Set());
  
  // Track brush range to preserve zoom across aggregation changes
  const [brushRange, setBrushRange] = useState<BrushRange | null>(null);

  const aggregationKeysOptions = useMemo(() => {
    const entries = Object.entries(AGGREGATION_KEYS).map(([key, value]) => ({
      label: value.label,
      value: key,
    }));
    return entries;
  }, [AGGREGATION_KEYS]);

  const [comparand, setComparand] = useState<TrendConfigs>({
    id: "comparand",
    label: AGGREGATION_KEYS[comparandAggregationKey].label,
    aggregationKey: defaultAggregationKey,
    aggregationFn: AGGREGATION_KEYS[comparandAggregationKey].defaultFn,
    aggregationKeysOptions: aggregationKeysOptions,
    handleAggregationKeyChange: (event) => {
      const aggregationKey = event.target
        .value as CityEscapeOverTimeChartAggregationKey;
      const keyConfig = AGGREGATION_KEYS[aggregationKey];
      setComparandAggregationKey(aggregationKey);
      setComparand((prev) => ({
        ...prev,
        label: keyConfig.label,
        aggregationKey,
        aggregationFn: keyConfig.defaultFn,
        yAxis: {
          ...prev.yAxis,
          label: buildYAxisLabel(keyConfig.label, keyConfig.defaultFn),
          valueFormatter: createValueFormatter(aggregationKey),
          minValue: keyConfig.minValue,
          maxValue: keyConfig.maxValue,
          positiveDirection: keyConfig.positiveDirection,
        },
      }));
    },
    handleAggregationFnChange: (_, value) => {
      const aggregationFn = value;
      setComparand((prev) => {
        const keyLabel =
          AGGREGATION_KEYS[
            prev.aggregationKey as CityEscapeOverTimeChartAggregationKey
          ]?.label ?? "";
        return {
          ...prev,
          aggregationFn,
          yAxis: {
            ...prev.yAxis,
            label: buildYAxisLabel(keyLabel, aggregationFn),
          },
        };
      });
    },
    yAxis: {
      id: "comparand",
      valueFormatter: createValueFormatter(comparandAggregationKey),
      label: buildYAxisLabel(
        AGGREGATION_KEYS[comparandAggregationKey].label,
        AGGREGATION_KEYS[comparandAggregationKey].defaultFn
      ),
      minValue: AGGREGATION_KEYS[comparandAggregationKey].minValue,
      maxValue: AGGREGATION_KEYS[comparandAggregationKey].maxValue,
      positiveDirection: AGGREGATION_KEYS[comparandAggregationKey].positiveDirection,
    },
  });

  const [comparator, setComparator] = useState<TrendConfigs>({
    id: "comparator",
    label: undefined,
    aggregationKey: undefined,
    aggregationFn: undefined,
    aggregationKeysOptions: aggregationKeysOptions,
    handleAggregationKeyChange: (event) => {
      const aggregationKey = event.target
        .value as CityEscapeOverTimeChartAggregationKey;
      const keyConfig = AGGREGATION_KEYS[aggregationKey];
      setComparator((prev) => ({
        ...prev,
        label: keyConfig.label,
        aggregationKey,
        aggregationFn: keyConfig.defaultFn,
        yAxis: {
          ...prev.yAxis,
          label: buildYAxisLabel(keyConfig.label, keyConfig.defaultFn),
          valueFormatter: createValueFormatter(aggregationKey),
          minValue: keyConfig.minValue,
          maxValue: keyConfig.maxValue,
          positiveDirection: keyConfig.positiveDirection,
        },
      }));
    },
    handleAggregationFnChange: (_, value) => {
      const aggregationFn = value;
      setComparator((prev) => {
        const keyLabel =
          AGGREGATION_KEYS[
            prev.aggregationKey as CityEscapeOverTimeChartAggregationKey
          ]?.label ?? "";
        return {
          ...prev,
          aggregationFn,
          yAxis: {
            ...prev.yAxis,
            label: buildYAxisLabel(keyLabel, aggregationFn),
          },
        };
      });
    },
    yAxis: {
      id: "comparator",
      label: undefined,
    },
  });

  const aggregationKeys = useMemo(() => {
    return [
      { key: comparand?.aggregationKey, yAxisId: comparand.id },
      { key: comparator?.aggregationKey, yAxisId: comparator.id },
    ].filter((v): v is AggregationKeyInput => Boolean(v.key));
  }, [comparand.aggregationKey, comparator.aggregationKey]);

  const entries = useCityEscapeTrendsOverTimeChartEntries(
    {
      selection,
      aggregationKeys,
      dataType,
      filters,
      opts: {
        request: {
          transferMode: WorkerQueryTransferModes.SHARED_ARRAY_BUFFER,
        },
        response: {
          transferMode: WorkerQueryTransferModes.SHARED_ARRAY_BUFFER,
          skipDecoding: true,
        },
      },
    },
    { enabled: !pending && aggregationKeys.length > 0 }
  );

  const trends: [TrendConfigs, TrendConfigs] = useMemo(() => {
    return [comparand, comparator];
  }, [comparand, comparator]);

  const matches = useAllMatches(
    {
      selection,
      filters,
      opts: {
        response: { transferMode: WorkerQueryTransferModes.ARRAY_BUFFER },
      },
    },
    { enabled: !pending }
  );
  const cityEscapes = useCityEscapesDatasource();
  const products = useProductsDatasource();

  const handleAreaClick = (event: any, data: any) => {
    console.log("Area click", event, data);
  };

  const handleAxisClick = (event: any, data: any) => {
    console.log("X Axis click", event, data);
  };

  const baseMetrics = useMemo<MetricConfig[]>(() => {
    const comparatorKey = comparator.aggregationKey as CityEscapeOverTimeChartAggregationKey | undefined;
    return getMetrics({
      dataType,
      selection,
      cityEscapes: cityEscapes.data,
      products: products.data,
      matches: matches.data,
      aggregationKeyLabel: AGGREGATION_KEYS[comparandAggregationKey]?.label,
      aggregationFn: comparand.aggregationFn,
      comparatorAggregationKeyLabel: comparatorKey ? AGGREGATION_KEYS[comparatorKey]?.label : undefined,
      comparatorAggregationFn: comparator.aggregationFn,
    });
  }, [
    matches.data,
    cityEscapes.data,
    products.data,
    selection,
    dataType,
    filters,
    comparandAggregationKey,
    comparand.aggregationFn,
    comparator.aggregationKey,
    comparator.aggregationFn,
  ]);

  // Initialize enabledMetrics on first render with default enabled state
  const isFirstRender = useRef(true);
  useEffect(() => {
    if (isFirstRender.current && baseMetrics.length > 0) {
      const initialEnabled = new Set(baseMetrics.filter(m => m.enabled).map(m => m.key));
      setEnabledMetrics(initialEnabled);
      isFirstRender.current = false;
    }
  }, [baseMetrics]);

  // Preserve enabled state across aggregation changes
  const metrics = useMemo<MetricConfig[]>(() => {
    if (enabledMetrics.size === 0) {
      // Not yet initialized, return as is
      return baseMetrics;
    }
    // Preserve enabled state from enabledMetrics set
    return baseMetrics.map(metric => ({
      ...metric,
      enabled: enabledMetrics.has(metric.key)
    }));
  }, [baseMetrics, enabledMetrics]);

  useEffect(() => {
    console.debug("CityEscapesTrendsOverTimeChart filters changed", {
      filters,
    });
  }, [filters]);

  useEffect(() => {
    setComparandAggregationKey(defaultAggregationKey);
  }, []);

  const cacheKey = useMemo(() => {
    return Math.random().toString(36).substring(2, 15);
  }, [
    selection,
    dataType,
    filters,
    // Removed comparand/comparator aggregationKey - changing visualization shouldn't reset state
  ]);

  const onAggregationKeyChange = useCallback(
    (key?: string) => {
      setComparandAggregationKey(key as CityEscapeOverTimeChartAggregationKey);
    },
    [setComparandAggregationKey]
  );

  const handleMetricsChange = useCallback((updatedMetrics: MetricConfig[]) => {
    const enabledKeys = new Set(updatedMetrics.filter(m => m.enabled).map(m => m.key));
    setEnabledMetrics(enabledKeys);
  }, []);

  const handleBrushRangeChange = useCallback((range: BrushRange | null) => {
    setBrushRange(range);
  }, []);

  return (
    <TrendsOverTimeChart
      version="dual"
      title="Metriche City Escape nel Tempo"
      metrics={metrics}
      loading={
        pending ||
        matches.isLoading ||
        cityEscapes.isLoading ||
        entries.isLoading
      }
      timelineData={entries.data ?? undefined}
      showAggregationSelector={true}
      defaultAggregationPeriod={defaultAggregationPeriod}
      defaultAggregationMode={defaultAggregationMode}
      aggregationKeysOptions={aggregationKeysOptions}
      onAggregationKeyChange={onAggregationKeyChange}
      onMetricsChange={handleMetricsChange}
      onBrushRangeChange={handleBrushRangeChange}
      brushRange={brushRange}
      defaultAggregationKey={comparandAggregationKey}
      trends={trends}
      defaultAggregationFn={AGGREGATION_KEYS[comparandAggregationKey].defaultFn}
      grid={grid}
      cacheKey={cacheKey}
      opts={{
        request: {
          transferMode: WorkerQueryTransferModes.SHARED_ARRAY_BUFFER,
        },
        response: {
          transferMode: WorkerQueryTransferModes.SHARED_ARRAY_BUFFER,
        },
      }}
    />
  );
}

function createValueFormatter(
  aggregationKey: CityEscapeOverTimeChartAggregationKey
) {
  return (value: any) => {
    const formatter =
      AGGREGATION_KEYS[aggregationKey as CityEscapeOverTimeChartAggregationKey]
        .valueFormatter;
    if (formatter) {
      const result = formatter(value);
      return typeof result === "string" ? result : String(result);
    }
    if (value == null) return "";
    if (typeof value === "number") {
      return value.toLocaleString("it-IT", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    }
    return String(value);
  };
}
